import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import AppPageTitle from "../../components/AppPageTitle";
import PermissionModal from "../../components/PermissionModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import apiV2 from "../../services/apiV2";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import useAsaasTransactions from "./hooks/useAsaasTransactions";
import TransactionSummaryCards from "./components/TransactionSummaryCards";
import TransactionFilters from "./components/TransactionFilters";
import TransactionsTable from "./components/TransactionsTable";
import TransactionDetailModal from "./components/TransactionDetailModal";

export default function Transactions() {
	const { user } = useContext(AuthContext);
	const history = useHistory();

	const [permissionOpen, setPermissionOpen] = useState(false);
	const [asaasConnected, setAsaasConnected] = useState(true);
	const [status, setStatus] = useState("all");
	const [period, setPeriod] = useState("0");
	const [searchParam, setSearchParam] = useState("");
	const [pageNumber, setPageNumber] = useState(1);
	const [selectedId, setSelectedId] = useState(null);
	const [detailOpen, setDetailOpen] = useState(false);

	const isAdmin = user?.profile === "admin";

	useEffect(() => {
		if (user && !isAdmin) {
			setPermissionOpen(true);
		}
	}, [user, isAdmin]);

	useEffect(() => {
		if (!isAdmin) return;

		async function checkIntegration() {
			try {
				const { data } = await apiV2.get("/integrations");
				const asaas = Array.isArray(data)
					? data.find((item) => item.name === "asaas")
					: null;
				setAsaasConnected(Boolean(asaas?.token));
			} catch (err) {
				toastError(err);
			}
		}

		checkIntegration();
	}, [isAdmin]);

	const { loading, error, summary, transactions, count, hasMore, pageSize, reload } =
		useAsaasTransactions({
			status,
			period,
			searchParam,
			pageNumber,
		});

	useEffect(() => {
		if (error) {
			toastError(error);
		}
	}, [error]);

	const handleStatusChange = (value) => {
		setStatus(value);
		setPageNumber(1);
	};

	const handlePeriodChange = (value) => {
		setPeriod(value);
		setPageNumber(1);
	};

	const handleSearchChange = (value) => {
		setSearchParam(value);
		setPageNumber(1);
	};

	const handleSelect = (item) => {
		setSelectedId(item.id);
		setDetailOpen(true);
	};

	const totalPages = Math.max(1, Math.ceil(count / pageSize));

	if (!isAdmin) {
		return (
			<PermissionModal
				open={permissionOpen}
				onClose={() => {
					setPermissionOpen(false);
					history.push("/");
				}}
			/>
		);
	}

	return (
		<div className="app-admin-page">
			<AppPageTitle
				title={i18n.t("transactions.title")}
				subtitle={i18n.t("transactions.subtitle")}
			/>

			<div className="app-admin-page__scroll">
				<div className="space-y-5">
					{!asaasConnected && (
						<div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
							{i18n.t("transactions.integrationMissing")}{" "}
							<Link to="/integrations" className="font-semibold underline">
								{i18n.t("transactions.goToIntegrations")}
							</Link>
						</div>
					)}

					<TransactionSummaryCards summary={summary} />

					<TransactionFilters
						status={status}
						period={period}
						searchParam={searchParam}
						onStatusChange={handleStatusChange}
						onPeriodChange={handlePeriodChange}
						onSearchChange={handleSearchChange}
					/>

					<TransactionsTable
						transactions={transactions}
						loading={loading}
						count={count}
						pageNumber={pageNumber}
						pageSize={pageSize}
						totalPages={totalPages}
						hasMore={hasMore}
						onPageChange={setPageNumber}
						onSelect={handleSelect}
					/>
				</div>
			</div>

			<TransactionDetailModal
				open={detailOpen}
				transactionId={selectedId}
				onClose={() => {
					setDetailOpen(false);
					setSelectedId(null);
					reload();
				}}
			/>
		</div>
	);
}
